import { container } from 'tsyringe';
import AuthenticationProviderEnum from '../../config/authenticationProviderEnum';
import { AnalyticsServiceInputType } from '../../services/AnalyticsService/types';
import { ApplicationContextEnum } from '../../services/applicationService/enums';
import { isNative as isNativeJWeb, JWebCoreFacade } from '../../services/JWeb';
import { TenantHandlerParamsType } from '../../services/tenantHandler/strategy/external-types';
import { TenantStrategyEnum } from '../../services/tenantHandler/strategy/strategy';
import { Stack } from '../../types/stratus';
import { PlatformEnum } from './enums';
import proccessWebManifestIntoAppManifest from './proccessWebManifestIntoAppManifest';
import * as T from './types';
import EventNames from '../../config/eventNames';

export default class ManifestToCommonsAdapter {
  public static convertToInterfaces(
    //TODO adding ManifestType here
    manifestInput: any
  ): T.CommonsInitializeInterfaceType {
    const manifest = ManifestToCommonsAdapter.getAdaptedManifest(manifestInput);
    const adapter: T.AdapterInputType = manifest?.adapter;

    // TODO: Update const analytics to remove duplicated code.
    const analytics: AnalyticsServiceInputType = {
      isNative: false, // At this point, I'm faking this value (it wont be used) but should be fixed.
      userActivity: manifest?.services?.userActivity,
      publishErrors: !!manifest?.services?.analytics?.publishErrors,
      enabled: !!manifest?.services?.analytics?.enable,
      batchEnabled: manifest?.services?.analytics?.batchEnabled,
      batchConfiguration: manifest?.services?.analytics?.batchConfiguration,
      queueConfiguration: manifest?.services?.analytics?.queueConfiguration,
      telemetryAPIkey: manifest?.services?.analytics?.telemetryAPIkey,
      customAnalyticsSubsystem:
        manifest?.services?.analytics?.customAnalyticsSubsystem,
      valveControllerAPIkey:
        manifest?.services?.analytics?.valveControllerAPIkey,
      stack: manifest?.portal?.stack as string
    };

    const fallbackConfiguration = {
      defaultFallbackKey: manifest?.fallback?.defaultFallbackKey,
      defaultFallback: {
        strategy: 'redirect',
        redirectTo: manifest?.portal?.fallbackRoute || '/',
        key: undefined
      },
      fallbackList: manifest?.fallback?.fallbackList
    };

    const breadcrumbInput: T.BreadcrumbInputType =
      manifest?.services?.breadcrumb;

    // @ts-ignore
    const stack: T.Stack = Stack[manifest?.portal?.stack];

    const accessControl: T.AccessControlInputType = {
      enabled: !!manifest?.services?.accessControl?.enabled,
      actionsPrefix: manifest?.services?.accessControl?.actionsPrefix,
      actions: manifest?.services?.accessControl?.actions || []
    };

    const entitlements: T.EntitlementsInputType = {
      enabled: !!manifest?.services?.entitlements?.enabled,
      stack: manifest?.portal?.stack
    };

    const onboarding: T.OnboardingInputType = {
      userOnboardingPath: manifest?.services?.onboarding?.userOnboardingPath,
      enable: !!manifest?.services?.onboarding?.enable,
      stack: manifest?.portal?.stack,
      stages: manifest?.services?.onboarding?.stages,
      ...manifest?.services?.onboarding
    };

    const optimizely: T.OptimizelyInputType = {
      enable: !!manifest?.services?.optimizely?.enable,
      snippetId: manifest?.services?.optimizely?.snippetId
    };

    const userOnboarding: T.UserOnboardingInputType = {
      enable: manifest?.services?.userOnboarding?.enable,
      path: manifest?.services?.userOnboarding?.path,
      forceUserOnboardingCriterionKey:
        manifest?.services?.userOnboarding?.forceUserOnboardingCriterionKey
    };

    // Setting the default values here
    const logger: T.LoggerInputType = {
      ...manifest?.services?.logger,
      allowedTypes: {
        debug: false,
        error: true,
        log: false,
        warn: true,
        ...manifest?.services?.logger?.allowedTypes
      },
      enableDecorators: !!manifest?.services?.logger?.enableDecorators
    };

    const criterions: T.CriterionInputType<any>[] = manifest?.criterions;

    const localization: T.LocalizationServiceInputType =
      manifest?.services?.localization;

    const authenticationProvider = manifest?.services?.login
      ?.authenticationProvider
      ? AuthenticationProviderEnum[
          manifest?.services?.login?.authenticationProvider
        ]
      : AuthenticationProviderEnum.authz;

    const login: T.LoginInputType = {
      enabled: !!manifest?.services?.login?.enable,
      assetReference: manifest?.services?.login?.assetReference,
      authenticationProvider,
      emailGathering: manifest?.services?.login?.emailGathering,
      properties: manifest?.services?.login?.properties,
      postLoginRedirect: manifest?.services?.login?.postLoginRedirect,
      preLoginValidation: manifest?.services?.login?.preLoginValidation,
      overrideClientOSAdditionalAuthorizationParams:
        manifest?.services?.login
          ?.overrideClientOSAdditionalAuthorizationParams,
      additionalAuthorizationParameters:
        manifest?.services?.login?.additionalAuthorizationParameters,
      syncAuthenticationDataOnJWebValueStore:
        manifest?.services?.login?.syncAuthenticationDataOnJWebValueStore
    };

    const navigation: T.NavigationServiceInputType = {
      useLegacyNavigation: manifest?.navigation?.useLegacyNavigation,
      removeLocaleUrl: manifest?.services?.localization?.removeLocaleUrl,
      blockNavigation: {
        pathList: manifest?.services?.navigation?.blockNavigation?.pathList
      }
    };

    const event: T.EventServiceInputType = {
      removeSelfFilterByContainerInstanceId:
        manifest?.services?.event?.removeSelfFilterByContainerInstanceId
    };

    const mock: T.CommonsMockType = {
      ...manifest?.mock,
      enable: !!manifest?.mock?.enable
    };

    return {
      mock,
      accessControl,
      adapter,
      analytics,
      breadcrumb: breadcrumbInput,
      criterions,
      entitlements,
      fallbackConfiguration,
      localization,
      logger,
      login,
      navigation,
      onboarding,
      optimizely,
      userOnboarding,
      stack,
      event
    };
  }

  public static async convert(
    manifestInput: any
  ): Promise<T.CommonsInitializeType> {
    const manifest = ManifestToCommonsAdapter.getAdaptedManifest(manifestInput);
    const appName = manifest?.portal?.appName;

    const basePath = manifest?.portal?.basePath;

    const clientId = manifest?.portal?.clientId;

    const emailGathering = manifest?.services?.login?.emailGathering || false;

    const isNative = await isNativeJWeb();

    if (isNative) {
      await proccessWebManifestIntoAppManifest(manifest);
    }

    const applicationContext = (() => {
      if (manifest?.portal?.platform === PlatformEnum.nativeOnly)
        return ApplicationContextEnum.NATIVE_APP;
      if (manifest?.portal?.clientId)
        return isNative
          ? ApplicationContextEnum.NATIVE_PORTAL
          : ApplicationContextEnum.WEB_PORTAL;
    })();

    const jWebPlatform = await JWebCoreFacade.getPlatform();

    const abTesting: T.ABTestingInputType = {
      enabled:
        !!manifest?.services?.abTesting?.enabled ||
        !!manifest?.services?.optimizely?.enable, // to keep retrocompatibility
      clientName: manifest?.services?.abTesting?.clientName,
      snippetId:
        manifest?.services?.abTesting?.snippetId ||
        manifest?.services?.optimizely?.snippetId // to keep retrocompatibility
    };

    const consent: T.ConsentServiceInputType = {
      isEmailGathering: emailGathering,
      isNative: isNative,
      globalInjectConsentBanner:
        manifest?.services?.consentService?.globalInjectConsentBanner ?? true
    };

    const localization: T.LocalizationServiceInputType =
      manifest?.services?.localization;

    const navigation: T.NavigationServiceInputType = {
      useLegacyNavigation: manifest?.navigation?.useLegacyNavigation,
      removeLocaleUrl: manifest?.services?.localization?.removeLocaleUrl,
      blockNavigation: {
        pathList: Array.isArray(
          manifest?.services?.navigation?.blockNavigation?.pathList
        )
          ? manifest?.services?.navigation?.blockNavigation?.pathList
          : []
      }
    };

    container.register('NavigationServiceProps', {
      useValue: navigation
    });

    const serviceWorker: T.ServiceWorkerInputType =
      manifest?.services?.serviceWorker;

    // @ts-ignore
    const stack: T.Stack = Stack[manifest?.portal?.stack];

    const theme: T.ThemeInputType = manifest?.theme;

    container.register('IsNative', {
      useValue: isNative
    });

    // TODO: Fix the retrocompatibility with orgSelector
    ManifestToCommonsAdapter.adaptOrgAndCustomerSelectorIntoTenantHandler(
      manifest
    );

    const analytics: AnalyticsServiceInputType = {
      isNative: isNative,
      userActivity: manifest?.services?.userActivity,
      publishErrors: !!manifest?.services?.analytics?.publishErrors,
      enabled: !!manifest?.services?.analytics?.enable,
      batchEnabled: manifest?.services?.analytics?.batchEnabled,
      batchConfiguration: manifest?.services?.analytics?.batchConfiguration,
      queueConfiguration: manifest?.services?.analytics?.queueConfiguration,
      telemetryAPIkey: manifest?.services?.analytics?.telemetryAPIkey,
      customAnalyticsSubsystem:
        manifest?.services?.analytics?.customAnalyticsSubsystem,
      valveControllerAPIkey:
        manifest?.services?.analytics?.valveControllerAPIkey,
      stack: manifest?.portal?.stack as string
    };

    const userActivity: T.UserActivityInputType =
      manifest?.services?.userActivity;

    const idleLogout: T.IdleLogoutInputType = manifest?.services?.idleLogout;

    const authenticationProvider = manifest?.services?.login
      ?.authenticationProvider
      ? AuthenticationProviderEnum[
          manifest?.services?.login?.authenticationProvider
        ]
      : AuthenticationProviderEnum.authz;

    const authProviders: T.AuthProvidersPropsType = {
      initialAuthenticationProvider:
        manifest?.services?.login?.initialAuthenticationProvider ||
        authenticationProvider,
      authenticationProvider: authenticationProvider
    };

    container.register('AuthProviderProps', {
      useValue: authProviders
    });

    const login: T.LoginInputType = {
      enabled: !!manifest?.services?.login?.enable,
      assetReference: manifest?.services?.login?.assetReference,
      authenticationProvider,
      emailGathering: emailGathering,
      overrideClientOSAdditionalAuthorizationParams:
        manifest?.services?.login
          ?.overrideClientOSAdditionalAuthorizationParams,
      additionalAuthorizationParameters:
        manifest?.services?.login?.additionalAuthorizationParameters,
      syncAuthenticationDataOnJWebValueStore:
        manifest?.services?.login?.syncAuthenticationDataOnJWebValueStore
    };
    const logger: T.LoggerInputType = {
      ...manifest?.services?.logger,
      isNative: isNative,
      enableDecorators: !!manifest?.services?.logger?.enableDecorators
    };

    const defaultLayoutKey = manifest?.navigation?.defaultLayoutKey;

    const defaultCriterionKey = manifest?.navigation?.defaultCriterionKey;

    const defaultNavigationElements =
      manifest?.navigation?.defaultNavigationElements;

    const ast: T.AstInputType = {
      enabled: !!manifest?.services?.ast?.enable
    };

    const featureFlags: T.FeatureFlagsInputType = {
      enabled: !!manifest?.services?.featureFlags?.enable,
      projectSettingsList: manifest?.services?.featureFlags?.clients
    };

    const routes: T.RoutesServiceDependenciesInputType = {
      defaultLayoutKey,
      routes: manifest?.navigation?.routes,
      defaultCriterionKey,
      defaultNavigationElements
    };

    const manifestTenantHandler = manifest?.services?.tenantHandler;

    const tenantHandlerProps: T.TenantHandlerInputType = {
      enabled: !!manifestTenantHandler?.enable,
      tenantHandlerList: manifestTenantHandler?.tenantHandlerList,
      globalTenantHandler: manifestTenantHandler?.globalTenantHandler,
      tenantSelectorAssetReference: manifestTenantHandler?.globalTenantHandler,
      stack: stack,
      isNative: isNative,
      tenantGroups: manifestTenantHandler?.tenantGroups
    };

    const monitoring: T.MonitoringInputType = {
      enabled: !!manifest?.services?.splunk?.enabled
    };

    const backgroundTasks: T.BackgroundTasksInputType = {
      enabled: !!manifest?.services?.backgroundTasks?.enable,
      backgroundTaskRegistrarList:
        manifest?.services?.backgroundTasks?.backgroundTaskRegistrarList
    };

    const grants: T.GrantsInputType = {
      enabled: manifest?.services?.grants?.enabled
    };
    const grantsHistory: T.GrantHistoryInputType = {
      enabled: manifest?.services?.grantsHistory?.enabled
    };

    const graphql: T.GraphQLServiceInputType = {
      mock: !!manifest?.services?.graphql?.mock,
      platform: manifest?.services?.graphql?.platform,
      isShellPortal: !!manifest?.services?.graphql?.isShellPortal,
      serverUrl: manifest?.services?.graphql?.serverUrl,
      stack: manifest?.portal?.stack,
      XApiKey: manifest?.services?.graphql?.XApiKey
    };

    const layouts: T.LayoutInputType[] = manifest?.layouts;

    const urlService: T.URLServiceInputType = {
      languageMap: localization?.languages,
      manifestBasePath: basePath,
      removeLocaleUrl: localization?.removeLocaleUrl
    };

    const onboarding: T.OnboardingInputType = {
      userOnboardingPath: manifest?.services?.onboarding?.userOnboardingPath,
      enable: !!manifest?.services?.onboarding?.enable,
      stack: manifest?.portal?.stack,
      ...manifest?.services?.onboarding
    };

    const userOnboarding: T.UserOnboardingInputType = {
      enable: manifest?.services?.userOnboarding?.enable,
      path: manifest?.services?.userOnboarding?.path,
      forceUserOnboardingCriterionKey:
        manifest?.services?.userOnboarding?.forceUserOnboardingCriterionKey,
      userOnboardingPath: manifest?.services?.onboarding?.userOnboardingPath // for backward compatibility
    };

    const shellFlags: T.ShellFlagsInputType = {
      oaLooseLoad: manifest?.services?.webPortalsFlags?.oaLooseLoad ?? true,
      nativeRefreshListener:
        manifest?.services?.webPortalsFlags?.nativeRefreshListener ?? true,
      nativeRefreshServicesOnAuthChange:
        manifest?.services?.webPortalsFlags
          ?.nativeRefreshServicesOnAuthChange ?? false,
      tenantSelectorSideMenuSkeleton:
        manifest?.services?.webPortalsFlags?.tenantSelectorSideMenuSkeleton ??
        true,
      enableTokenRefreshOnInit:
        manifest?.services?.webPortalsFlags?.enableTokenRefreshOnInit ?? false,
      forceDefaultAdditionalAuthorizationParams:
        manifest?.services?.webPortalsFlags
          ?.forceDefaultAdditionalAuthorizationParams ?? false
    };

    container.register<T.ShellFlagsInputType>('ShellFlagsProps', {
      useValue: shellFlags
    });

    container.register<T.LoggerInputType>('LoggerProps', {
      useValue: logger
    });

    container.register('SupportSessionProps', {
      useValue: { initialEnablementStatus: ast.enabled }
    });

    container.register('TenantHandlerProps', {
      useValue: tenantHandlerProps
    });

    container.register('LoginProps', {
      useValue: login
    });

    const platform: PlatformEnum =
      manifest?.portal?.platform || PlatformEnum.auto;

    container.register('Platform', { useValue: platform });

    const event: T.EventServiceInputType = {
      removeSelfFilterByContainerInstanceId:
        manifest?.services?.event?.removeSelfFilterByContainerInstanceId
    };
    container.register('EventServiceProps', { useValue: event });

    return {
      abTesting,
      platform,
      analytics,
      appName,
      ast,
      backgroundTasks,
      basePath,
      clientId,
      consent,
      defaultCriterionKey,
      defaultLayoutKey,
      featureFlags,
      grants,
      grantsHistory,
      jWebPlatform,
      isNative,
      layouts,
      localization,
      login,
      logger,
      monitoring,
      navigation,
      routes,
      serviceWorker,
      stack,
      theme,
      urlService,
      userActivity,
      idleLogout,
      graphql,
      event,
      applicationContext,
      onboarding,
      userOnboarding
    };
    // TODO: we should remove this "as" to force casting here.
  }

  // TODO: Retrocompatibility behavior
  public static adaptOrgAndCustomerSelectorIntoTenantHandler(
    manifest: any
  ): void {
    const DEFAULT_TENANT_HANDLER_KEY = 'default';
    const isTenantHandlerActive = !!manifest?.services?.tenantHandler?.enable;

    if (isTenantHandlerActive) return;

    const orgSelectorSettings = manifest?.navigation?.header?.domainSelector;

    if (orgSelectorSettings?.enable) {
      const orgSelectorToTenantHandlerSettings: TenantHandlerParamsType = {
        key: DEFAULT_TENANT_HANDLER_KEY,
        strategy: TenantStrategyEnum.stratusOrganization,
        options: {
          fallback: orgSelectorSettings?.fallback,
          filter: orgSelectorSettings?.filter
        }
      };

      manifest.services['tenantHandler'] = {
        enable: true,
        globalTenantHandler: orgSelectorToTenantHandlerSettings.key,
        tenantHandlerList: [orgSelectorToTenantHandlerSettings]
      };

      // TODO: This is very fragile, the portal could change the assetReference to any other name.
      const customerSelectorSettings =
        manifest?.navigation?.header?.widgets?.find(
          (w) =>
            w.assetReference === '@jarvis/react-shell-customer-selector' &&
            w.enable
        );

      // Customer Selector Behavior
      if (customerSelectorSettings?.enable) {
        const customerSelectorToTenantHandlerSettings: TenantHandlerParamsType =
          {
            strategy: TenantStrategyEnum.stratusCustomer
          };

        orgSelectorToTenantHandlerSettings['subTenant'] =
          customerSelectorToTenantHandlerSettings;
      }
    }
  }

  private static _getAdaptedManifestCache: any;
  private static getAdaptedManifest(manifestInput: any) {
    if (!ManifestToCommonsAdapter._getAdaptedManifestCache) {
      const manifest = JSON.parse(JSON.stringify(manifestInput));
      ManifestToCommonsAdapter._getAdaptedManifestCache = manifest;

      if (manifest?.navigation?.routes?.length) {
        const nativeRoutes: T.NavigationServiceInputType['blockNavigation']['pathList'] =
          [];

        const handleRoute = (routes: any[], isSubRoute = false): any[] => {
          return routes.filter((route) => {
            if (route?.isNative) {
              if (isSubRoute) {
                console.error(
                  `Manifest error: route path (${route?.path}) is native and cannot be used as subRoute.`
                );
              } else {
                if (!route?.path) {
                  console.error(
                    'Manifest error: route.path is required for native routes'
                  );
                } else {
                  if (!route?.eventName && !route?.serviceId) {
                    console.error(
                      `Manifest error: native route (${route.path}) does not have eventName or serviceId.`
                    );
                  }
                  nativeRoutes.push({
                    path: route?.path,
                    exact: route?.exact,
                    eventName: route?.eventName,
                    serviceId: route?.serviceId,
                    properties: route?.properties
                  });
                }
              }
            } else {
              if (route?.subRoutes) {
                route.subRoutes = handleRoute(route.subRoutes, true);
                if (route?.subRoutes?.length === 0) {
                  delete route.subRoutes;
                }
              }

              return true;
            }
            return false;
          });
        };
        manifest.navigation.routes = handleRoute(
          manifest?.navigation?.routes,
          false
        );

        if (nativeRoutes.length) {
          if (
            manifest.services?.navigation?.blockNavigation?.pathList?.length
          ) {
            manifest.services.navigation.blockNavigation.pathList =
              manifest.services.navigation.blockNavigation.pathList
                .map(
                  (
                    value:
                      | T.NavigationServiceInputBlockNavigationPathListObjectType
                      | string
                  ): T.NavigationServiceInputBlockNavigationPathListObjectType => {
                    const eventName = EventNames.clientosNavigationBlocked;
                    if (typeof value === 'string') {
                      return { path: value, eventName };
                    } else {
                      if (!value?.path) {
                        console.error(
                          'Manifest error: blockNavigation.pathList.path is required'
                        );
                        return;
                      }
                    }
                    return {
                      eventName: eventName,
                      ...value
                    };
                  }
                )
                .filter(Boolean);
          }
          manifest.services = {
            ...(manifest.services || {}),
            navigation: {
              ...(manifest.services?.navigation || {}),
              blockNavigation: {
                ...(manifest.services?.navigation?.blockNavigation || {}),
                pathList: [
                  ...(manifest.services?.navigation?.blockNavigation
                    ?.pathList || []),
                  ...nativeRoutes
                ]
              }
            }
          };
        }
      }

      return manifest;
    }
    return ManifestToCommonsAdapter._getAdaptedManifestCache;
  }
}
